import { type ReactNode, useEffect, useState } from 'react'
import { TrendingUp, Rocket, Target, BarChart3, Users } from 'lucide-react'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'
import Image from 'next/image'
import { SectionHeader } from '../SectionHeader'
import type { GrowthPillarsData } from '@/data/solutions/types'

type GrowthPillarsProps = GrowthPillarsData

// Icon mapping function
const getIcon = (iconName: string): ReactNode => {
  const iconMap: Record<string, ReactNode> = {
    Target: <Target className="h-6 w-6 " />,
    Rocket: <Rocket className="h-6 w-6 " />,
    Users: <Users className="h-6 w-6 " />,
    BarChart3: <BarChart3 className="h-6 w-6 " />
  }
  return iconMap[iconName] || <Target className="h-6 w-6 " />
}

export const GrowthPillars = ({ heading, pillars }: GrowthPillarsProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'center' },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  )
  const [selected, setSelected] = useState(0)

  useEffect(() => {
    if (!emblaApi) return
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap())
    emblaApi.on('select', onSelect)
    onSelect()
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi])

  const scrollTo = (index: number) => emblaApi?.scrollTo(index)

  return (
    <div className="flex flex-col gap-8">
      <SectionHeader
        label="Growth Pillars"
        icon={<TrendingUp className="h-4 w-4" />}
        heading={heading}
        color="inc-primary-10"
      />
      {/* Embla Carousel */}
      <div className="relative">
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex">
            {pillars.map((pillar, index) => {
              return (
                <div key={index} className="flex-[0_0_100%] min-w-0 px-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
                    <div
                      className={`px-5 md:px-10 py-10 md:py-16 | flex flex-col items-start gap-4 | ${pillar.bg} | rounded-4xl`}
                    >
                      <span>{getIcon(pillar.icon)}</span>
                      <h3 className="text-2xl md:text-3xl font-semibold">
                        {pillar.title}
                      </h3>
                      <p className="text-left text-base md:text-2xl">
                        {pillar.description}
                      </p>
                    </div>
                    <div className="flex items-center justify-center">
                      <Image
                        src={pillar.image}
                        alt=""
                        width={548}
                        height={528}
                      />
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2">
        {pillars.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            aria-label={`Go to slide ${index + 1}`}
            className={`w-2 h-2 rounded-full transition-all ${
              selected === index ? 'bg-black' : 'border border-black'
            }`}
          />
        ))}
      </div>
    </div>
  )
}
